import React, { FormEvent, useEffect, useState } from 'react'
import { Form } from 'react-router-dom';
import { Alert, Button, IconButton, MenuItem, Select, TextField } from '@mui/material';

import { ITransporterVendor } from '../../types/transporter';
import { ISelectOption } from '../../types/list';

import { TransporterService } from '../../services/transporterService';
import { UtilityService } from '../../services/utilityService';

import Spinner from '../spinner';

import '../../styles/transporterVendorForm.scss';
import { Icons } from '../icons';
import { VendorService } from '../../services/vendorService';
import { IVendorLight } from '../../types/vendor';

export interface ITransporterVendorsFormProps {
	id: string;
	vendors: ITransporterVendor[];
}

const TransporterVendorForm: React.FunctionComponent<ITransporterVendorsFormProps> = ({ id, vendors }) => {
	const [transporterVendors, setTransporterVendors] = useState<ITransporterVendor[]>(vendors);
	const [unassignedVendors, setUnassignedVendors] = useState<ISelectOption[]>([]);
	const [vendorIdField, setVendorIdField] = useState("");
	const [distanceField, setDistanceField] = useState(0);
	const [isLoadingVendors, setIsLoadingVendors] = useState(false);
	const [isAdding, setIsAdding] = useState(false);
	const [isRemoving, setIsRemoving] = useState(false);
	const [isRemovingVendorId, setIsRemovingVendorId] = useState("");
	const [errorMessage, setErrorMessage] = useState<string | undefined>();

	const loadVendors = async () => {
		setIsLoadingVendors(true);

		const response = await VendorService.getActiveVendors();
		const allVendors = (response.data as IVendorLight[]).map<ISelectOption>((vendor) => { return { name: vendor.name, value: vendor.id.toString(), isActive: true } });

		let unassigned: ISelectOption[] = allVendors.filter((t: ISelectOption) => {
			let isUnassigned = true;

			if (transporterVendors) {
				vendors.map((vc) => {
					if (vc.vendorId.toString() === t.value) isUnassigned = false;
				})
			}

			return isUnassigned;
		}).slice();

		unassigned.sort((a, b) => UtilityService.compareStrings(a.name, b.name))

		setUnassignedVendors(unassigned);
		setVendorIdField(unassigned[0].value ? unassigned[0].value : "");
		setIsLoadingVendors(false);
	}

	useEffect(() => {
		loadVendors();
	}, [vendors])

	const handleAdd = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault;
		setIsAdding(true);

		if (vendorIdField && vendorIdField != "") {
			const newTv: ITransporterVendor = {
				id: 0,				
				transporterId: +id,
				transporterName: '',
				vendorId: +vendorIdField,
				vendorName: '',
				vendorState: '',
				distance: distanceField
			};
			TransporterService.addVendor(id, newTv)
			.then((response) => {
				console.log('TransporterService.addVendor: SUCCESS', response);

				let tv: ITransporterVendor = {
					transporterName: response.data.transporterName,
					vendorId: response.data.vendorId,
					vendorName: response.data.vendorName,
					vendorState: response.data.vendorState,
					distance: response.data.distance,
					id: response.data.id,
					transporterId: response.data.transporterId
				}

				var newUnassignedVendors = unassignedVendors.filter((item) => { return item.value != vendorIdField });

				vendors.push(tv); // Sync with source
				setTransporterVendors([...vendors]);				
				setUnassignedVendors(newUnassignedVendors);
				setVendorIdField(newUnassignedVendors[0].value ? newUnassignedVendors[0].value : "");
				setDistanceField(0);
			})
			.catch((error) => {
				console.log('TransporterService.addVendor: ERROR', error);
				setErrorMessage("We encountered an error while processing your request. Please try again.");
			})
			.finally(() => {
				setIsAdding(false);
			});
		}
	}

	const handleRemove = async (vendorId: string) => {
		setIsRemoving(true);
		setIsRemovingVendorId(vendorId);
		if (vendorId) {
			TransporterService.removeVendor(id, vendorId)
			.then((response) => {
				console.log('TransporterService.removeVendor: SUCCESS', response);

				var removedVendor = transporterVendors.filter((item) => { return item.vendorId.toString() == vendorId }).map((item) => {
					let s: ISelectOption = {
						name: item.vendorName,
						value: item.vendorId.toString()
					}
					return s;
				});
				
				var newUnassignedVendors = [...unassignedVendors, ...removedVendor];
				vendors.splice(vendors.indexOf(vendors.find(v => v.id == +vendorId)), 1); // Sync with source
				
				setUnassignedVendors(newUnassignedVendors.sort((a, b) => UtilityService.compareStrings(a.name, b.name)));
				setTransporterVendors(transporterVendors.filter((item) => { return item.vendorId.toString() != vendorId }));
				setVendorIdField(newUnassignedVendors[0].value);
			})
			.catch((error) => {
				console.log('TransporterService.removeVendor: ERROR', error);
				setErrorMessage("We encountered an error while processing your request. Please try again.");
			})			
			.finally(() => {
				setIsRemoving(false);
				setIsRemovingVendorId("");
			});
		}
	}

	return (
		<section className="hwi-transporter-vendor-form">
			<Spinner dependency={isLoadingVendors} text='Loading vendors...'>
				<div className="facility-table">
					<div className="headers">
						<div>Vendor</div>
						<div>Distance</div>
						<div>State</div>
						<div></div>
					</div>
					<div className="body">
						{transporterVendors.length > 0 ? (
							<>
								{transporterVendors.map((item, index) => (
									<div className="row" key={index}>
										<div>{item.vendorName}</div>
										<div>{item.distance}</div>
										<div>{item.vendorState}</div>
										<div>
											<Button onClick={() => handleRemove(item.vendorId.toString())} disabled={isRemoving}>{isRemoving && isRemovingVendorId == item.vendorId.toString() ? "Removing..." : (<><Icons.RemoveItem fill="#a82525" height="16px"></Icons.RemoveItem><span>&nbsp;Remove</span></>)}</Button>
										</div>
									</div>
								))}
							</>
						) : (
							<div className="no-results">0 results</div>
						)}

					</div>
				</div>
				{errorMessage &&
					<Alert className="alert-error" severity="error" action={<IconButton onClick={() => { setErrorMessage(undefined); }} size='small'>X</IconButton>}>{errorMessage}</Alert>
				}
				<Form onSubmit={(event) => handleAdd(event)}>
					<div className="row">
						<div>
							<Select
								required
								className="status-field"
								value={vendorIdField}
								fullWidth
								onChange={(event) => setVendorIdField(event.target.value)}
							>
								{unassignedVendors.map((item, index) => (
									<MenuItem value={item.value} key={index} >{item.name}</MenuItem>
								))}
							</Select>

						</div>
						<div>
							<TextField
								className="status-field"
								name="distance"
								placeholder="Distance (mi)"
								required
								type="number"
								InputProps={{ inputProps: { min: 0 } }}
								value={distanceField}
								size="small"
								onChange={(event) => { setDistanceField(parseInt(event.target.value)) }}>
							</TextField>
						</div>
						<div>
							{/* {selectedVendor?.shippingAddress?.state} */}
						</div>
						
						<div className="controls">
							<Button type="submit" variant="text" className="save-button" disabled={isAdding}>{isAdding ? "Adding..." : (<><Icons.AddItem fill="#272f50" height="16px"></Icons.AddItem><span>&nbsp;Add</span></>)}</Button>
						</div>
					</div>
				</Form>
			</Spinner>
		</section>
	)
}

export default TransporterVendorForm;